import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, waLink } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

function ServiceCard({ service, index, photo }) {
  const num = String(index + 1).padStart(2, "0");
  return (
    <motion.a
      data-testid={`service-card-${index + 1}`}
      href={waLink(`Hello Vayu Celebrations! I'd like a quote for ${service.title}.`)}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay: (index % 4) * 0.1, duration: 0.9, ease }}
      className={`group relative block ${index % 2 === 1 ? "lg:mt-14" : ""}`}
    >
      <div className="relative overflow-hidden rounded-lg border border-gold-faint bg-ivory p-2.5 transition-all duration-500 group-hover:-translate-y-2 group-hover:shadow-[0_30px_60px_-30px_rgba(176,141,66,0.55)]">
        <div className="relative h-72 overflow-hidden rounded-md sm:h-80">
          <img
            src={photo ? photo.url : service.img}
            alt={service.title}
            loading="lazy"
            className="h-full w-full object-cover saturate-[0.82] transition-all duration-700 group-hover:scale-105 group-hover:saturate-100"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-night/55 via-transparent to-transparent opacity-70 transition-opacity duration-500 group-hover:opacity-90" />
          {index === 0 && (
            <span data-testid="speciality-badge" className="absolute left-4 top-4 rounded-full bg-gold px-3 py-1 text-[9px] font-bold uppercase tracking-[0.25em] text-night shadow-[0_8px_20px_-6px_rgba(176,141,66,0.8)]">
              Our Speciality
            </span>
          )}
          <span className="absolute right-4 top-4 font-serif text-sm italic tracking-widest text-cream/90">
            {num} —
          </span>
          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="font-serif text-2xl font-medium text-cream">{service.title}</h3>
          </div>
        </div>
        <div className="flex items-start justify-between gap-3 px-2.5 py-4">
          <p className="text-sm leading-relaxed text-clay">{service.caption}</p>
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gold-faint text-gold-dark transition-all duration-300 group-hover:bg-gold group-hover:text-night">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </motion.a>
  );
}

export default function Services() {
  const [photos, setPhotos] = useState({});

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then((r) => {
        const map = {};
        r.data.forEach((p) => {
          if (!map[p.service] || p.created_at > map[p.service].created_at) map[p.service] = p;
        });
        setPhotos(map);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="services" data-testid="services-section" className="relative bg-parch py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease }}
          className="mb-14 flex flex-col justify-between gap-6 sm:mb-20 md:flex-row md:items-end"
        >
          <div>
            <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.35em] text-gold-dark">
              Our Services
            </p>
            <h2 className="font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl">
              Everything your celebration
              <br />
              <span className="italic text-gold-dark">could ask for.</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-clay">
            Eight signature services, one obsession — detail. Tap any service to get a quote on
            WhatsApp within minutes.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.title} service={s} index={i} photo={photos[s.title]} />
          ))}
        </div>
      </div>
    </section>
  );
}
